import { Box, Card, CardContent, Grid, Typography, Stack } from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import type { Student } from '../api/students';
import { calculateAverageWaitTime, formatWaitTime } from '../utils/waitTime';

interface DismissalStatsProps {
  students: Student[];
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  color: string;
}

function StatCard({ icon, label, value, color }: StatCardProps) {
  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack spacing={1}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: `${color}15`,
                color: color,
              }}
            >
              {icon}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {label}
            </Typography>
          </Box>
          <Typography
            variant="h4"
            fontWeight={700}
            sx={{
              fontSize: { xs: '1.75rem', sm: '2rem' },
              lineHeight: 1,
              ml: 0.5
            }}
          >
            {value}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function DismissalStats({ students }: DismissalStatsProps) {
  const waitingStudents = students.filter(s => !s.dismissed_at);
  const carRiders = waitingStudents.filter(s => s.pickup_type === 'car').length;
  const walkers = waitingStudents.filter(s => s.pickup_type === 'foot').length;
  const avgWaitTime = calculateAverageWaitTime(students);

  return (
    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
      <Grid item xs={6} sm={6} md={3}>
        <StatCard
          icon={<GroupIcon sx={{ fontSize: 20 }} />}
          label="Total Waiting"
          value={waitingStudents.length}
          color="#1976d2"
        />
      </Grid>
      <Grid item xs={6} sm={6} md={3}>
        <StatCard
          icon={<DirectionsCarIcon sx={{ fontSize: 20 }} />}
          label="Car Riders"
          value={carRiders}
          color="#00897B"
        />
      </Grid>
      <Grid item xs={6} sm={6} md={3}>
        <StatCard
          icon={<DirectionsWalkIcon sx={{ fontSize: 20 }} />}
          label="Walkers"
          value={walkers}
          color="#5D4037"
        />
      </Grid>
      <Grid item xs={6} sm={6} md={3}>
        <StatCard
          icon={<AccessTimeIcon sx={{ fontSize: 20 }} />}
          label="Avg Wait Time"
          value={avgWaitTime > 0 ? formatWaitTime(avgWaitTime) : '—'}
          color="#F57C00"
        />
      </Grid>
    </Grid>
  );
}
